import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Modal, notification } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { format, addDays } from 'date-fns';
import CadastrePublicoFormService, {
    CadastroOpcaoItem,
    CadastroCidadeItem,
} from '../../../../services/http/services/CadastrePublico/CadastrePublicoFormService';
import { convertDataBrParaUS, convertDataEHoraBrParaUS } from '../../../../services/Conversores';
import { mask, apenasDigitosCelular, CELULAR_DIGITOS_BR } from '../../../../utils/MascaraDeCampos';
import { removeAcento } from '../../../../utils/RemoveAcentos';

export type UseFormSemContratoParams = {
    initialVendedorId?: number;
    vendedorLocked?: boolean;
    onSuccess?: () => void;
};

export function useFormSemContrato({ initialVendedorId, vendedorLocked, onSuccess }: UseFormSemContratoParams) {
    const [form] = Form.useForm();
    const refClickSubmitSalvar = useRef(false);
    const refApertouSelectCidade = useRef(0);
    const refDadosPoderList = useRef<CadastroOpcaoItem[]>([]);
    const refDadosCargoList = useRef<CadastroOpcaoItem[]>([]);

    const [loadingInit, setLoadingInit] = useState(true);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [loadingPoder, setLoadingPoder] = useState(false);
    const [loadingVendedores, setLoadingVendedores] = useState(false);
    const [loadingCidades, setLoadingCidades] = useState(false);

    const [dadosPoderList, setDadosPoderList] = useState<CadastroOpcaoItem[]>([]);
    const [dadosCargoList, setDadosCargoList] = useState<CadastroOpcaoItem[]>([]);
    const [dadosVendedoresList, setDadosVendedoresList] = useState<CadastroOpcaoItem[]>([]);
    const [dataListCidades, setDataListCidades] = useState<CadastroCidadeItem[]>([]);
    const [paginaAtualCidades, setPaginaAtualCidades] = useState(1);
    const temMaisCidadesRef = useRef(true);
    const [buscaCidades, setBuscaCidades] = useState('');
    const [isSelectCidadeOpen, setIsSelectCidadeOpen] = useState(false);

    // Campo validade_teste (payload): sem input na tela; valor vem deste estado (padrão +30 dias no carregar).
    const [selectedDate, setSelectedDate] = useState<any>(null);
    // Campo data_proximo_contato (payload): sem input na tela; valor vem deste estado (padrão +30 dias no carregar).
    const [selectedDateProximoContato, setSelectedDateProximoContato] = useState<any>(null);
    const [msgUsuarioJaExiste, setMsgUsuarioJaExiste] = useState(false);
    const [vendedorSelectBloqueado, setVendedorSelectBloqueado] = useState(false);

    const encontrarCargoPorId = useCallback((id: number) => refDadosCargoList.current.find((e) => e.id === id), []);
    const encontrarPoderPorId = useCallback((id: number) => refDadosPoderList.current.find((e) => e.id === id), []);

    const fetchCidades = useCallback(async (pagina = 1, nome = '') => {
            if (!temMaisCidadesRef.current && pagina !== 1) return;
            try {
                setLoadingCidades(true);
                const data = await CadastrePublicoFormService.getCidades(pagina, nome);
                if (pagina === 1) {
                    setDataListCidades(data);
                } else {
                    setDataListCidades((prev) => [...prev, ...data]);
                }
                const mais = data.length === 10;
                temMaisCidadesRef.current = mais;
                setPaginaAtualCidades(pagina);
            } catch (error: any) {
                if (error?.message !== 'Requisicao cancelada!') {
                    notification.error({
                        message: 'Erro',
                        description: 'Não foi possível buscar cidades!',
                    });
                }
            } finally {
                setLoadingCidades(false);
            }
        },
        []
    );

    const buscarCelularSeJaExiste = useCallback(
        async (celular: string): Promise<boolean> => {
            const digits = apenasDigitosCelular(celular);
            if (digits.length !== CELULAR_DIGITOS_BR) {
                return false;
            }
            try {
                const result = await CadastrePublicoFormService.getUsuarioPorCelular(digits);
                if (typeof result === 'string') {
                    setMsgUsuarioJaExiste(false);
                    if (!refClickSubmitSalvar.current) {
                        form.setFieldsValue({ nome: '' });
                    }
                    return false;
                }
                if (!refClickSubmitSalvar.current) {
                    form.setFieldsValue({
                        nome: (result as any).nome,
                        // Campo cargo_id: setando valor retornado pela API (celular já cadastrado).
                        cargo_id: (result as any).cargo?.id,
                    });
                }
                setMsgUsuarioJaExiste(true);
                return false;
            } catch (error) {
                notification.error({
                    message: 'Erro',
                    description: 'Não foi possível consultar o celular!',
                });
                return false;
            }
        },
        [form]
    );

    const onChangeInputTelefone = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const originalValue = apenasDigitosCelular(e.target.value);
            const maskedValue = mask(originalValue, ['(99) 9 9999-9999']);
            form.setFieldsValue({ celular: maskedValue });
            if (originalValue.length === CELULAR_DIGITOS_BR) {
                form.validateFields(['celular']).catch(() => undefined);
            }
        },
        [form]
    );

    const onDropdownVisibleChangeCidades = useCallback(
        (open: boolean) => {
            if (refApertouSelectCidade.current !== 1) {
                setIsSelectCidadeOpen(open);
            }
            if (open) {
                temMaisCidadesRef.current = true;
                setPaginaAtualCidades(1);
                setBuscaCidades('');
                fetchCidades(1, '');
                if (refApertouSelectCidade.current === 0) {
                    form.setFieldsValue({ cidade_id: undefined });
                }
            }
            refApertouSelectCidade.current++;
        },
        [fetchCidades, form]
    );

    const handleSearchCidades = useCallback(
        (value: string) => {
            setBuscaCidades(value);
            temMaisCidadesRef.current = true;
            fetchCidades(1, value);
        },
        [fetchCidades]
    );

    const handleScrollCidades = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            if (scrollTop + clientHeight >= scrollHeight - 5 && !loadingCidades) {
                fetchCidades(paginaAtualCidades + 1, buscaCidades);
            }
        },
        [buscaCidades, fetchCidades, loadingCidades, paginaAtualCidades]
    );

    useEffect(() => {
        let ativo = true;

        const carregar = async () => {
            setLoadingInit(true);
            try {
                const dataSomada = format(addDays(new Date(), 30), 'yyyy-MM-dd');
                const dataEHoraSomada = format(addDays(new Date(), 30), 'yyyy-MM-dd HH:mm');
                // Campo validade_teste (payload): setando valor padrão +30 dias (sem input na tela).
                setSelectedDate(moment(dataSomada));
                // Campo data_proximo_contato (payload): setando valor padrão +30 dias (sem input na tela).
                setSelectedDateProximoContato(moment(dataEHoraSomada));

                setLoadingPoder(true);
                setLoadingVendedores(true);

                const [poderes, cargos, vendedores] = await Promise.all([
                    CadastrePublicoFormService.getPoderes(),
                    CadastrePublicoFormService.getCargos(),
                    CadastrePublicoFormService.getVendedores(),
                ]);

                if (!ativo) return;

                setDadosPoderList(poderes);
                refDadosPoderList.current = poderes;
                setDadosCargoList(cargos);
                refDadosCargoList.current = cargos;
                setDadosVendedoresList(vendedores);

                form.setFieldsValue({
                    // Campo poder_id: setando valor padrão (altere o id se precisar outro poder).
                    poder_id: 2,
                    // Campo cargo_id: setando valor padrão (altere o id se precisar outro cargo).
                    cargo_id: 28,
                    // Campo enviar_whatsapp: setando valor (sem input na tela nesta página).
                    enviar_whatsapp: true,
                    // Campo enviar_contato_vendedor: setando valor (sem input na tela nesta página).
                    enviar_contato_vendedor: true,
                });

                const vendedorValidoComLink =
                    Boolean(vendedorLocked) &&
                    typeof initialVendedorId === 'number' &&
                    !Number.isNaN(initialVendedorId) &&
                    vendedores.some((v) => v.id === initialVendedorId);

                if (vendedorValidoComLink) {
                    // Campo vendedor_id: setando valor pela URL (?vendedor_id=) quando o id existe na lista.
                    form.setFieldsValue({ vendedor_id: initialVendedorId });
                    setVendedorSelectBloqueado(true);
                } else {
                    setVendedorSelectBloqueado(false);
                }

                if (ativo) {
                    await fetchCidades(1, '');
                }
            } catch (error: any) {
                if (!ativo) return;
                notification.error({
                    message: 'Erro',
                    description:
                        'Não foi possível carregar o formulário. Tente novamente mais tarde ou entre em contato com o suporte.',
                });
            } finally {
                if (ativo) {
                    setLoadingPoder(false);
                    setLoadingVendedores(false);
                    setLoadingInit(false);
                }
            }
        };
        carregar();
        return () => {
            ativo = false;
        };
        // Uma carga por montagem; ?vendedor_id= remonta o form via key no pai (CadastreSe).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const confirmarPoderCargo = useCallback(
        (nomePoder: string | undefined, nomeCargo: string | undefined, onOk: () => void) => {
            if (nomePoder === 'Executivo') {
                if (nomeCargo === 'Vereador(a)') {
                    Modal.confirm({
                        className: 'modalContratoAtivoInativo',
                        title: 'Cuidado',
                        icon: <ExclamationCircleOutlined />,
                        content:
                            'O usuário cadastrado é Vereador no poder Executivo! Tem certeza que deseja continuar?',
                        okText: 'Sim',
                        okType: 'danger',
                        cancelText: 'Rever campos',
                        onOk,
                    });
                    return;
                }
                if (nomeCargo === 'Presidente da Câmara') {
                    Modal.confirm({
                        className: 'modalContratoAtivoInativo',
                        title: 'Cuidado',
                        icon: <ExclamationCircleOutlined />,
                        content:
                            'O usuário cadastrado é Presidente da Câmara no poder Executivo! Tem certeza que deseja continuar?',
                        okText: 'Sim',
                        okType: 'danger',
                        cancelText: 'Rever campos',
                        onOk,
                    });
                    return;
                }
            }
            if (nomePoder === 'Legislativo') {
                if (nomeCargo === 'Prefeito(a)') {
                    Modal.confirm({
                        className: 'modalContratoAtivoInativo',
                        title: 'Cuidado',
                        icon: <ExclamationCircleOutlined />,
                        content:
                            'O usuário cadastrado é Prefeito(a) no poder Legislativo! Tem certeza que deseja continuar?',
                        okText: 'Sim',
                        okType: 'danger',
                        cancelText: 'Rever campos',
                        onOk,
                    });
                    return;
                }
                if (nomeCargo === 'Ex-Prefeito(a)') {
                    Modal.confirm({
                        className: 'modalContratoAtivoInativo',
                        title: 'Cuidado',
                        icon: <ExclamationCircleOutlined />,
                        content:
                            'O usuário cadastrado é Ex-Prefeito(a) no poder Legislativo! Tem certeza que deseja continuar?',
                        okText: 'Sim',
                        okType: 'danger',
                        cancelText: 'Rever campos',
                        onOk,
                    });
                    return;
                }
                if (nomeCargo === 'Vice Prefeito(a)') {
                    Modal.confirm({
                        className: 'modalContratoAtivoInativo',
                        title: 'Cuidado',
                        icon: <ExclamationCircleOutlined />,
                        content:
                            'O usuário cadastrado é Vice Prefeito(a) no poder Legislativo! Tem certeza que deseja continuar?',
                        okText: 'Sim',
                        okType: 'danger',
                        cancelText: 'Rever campos',
                        onOk,
                    });
                    return;
                }
            }
            onOk();
        },
        []
    );

    const salvar = useCallback(
        async (values: Record<string, unknown>) => {
            try {
                setLoadingSubmit(true);
                const payload: Record<string, unknown> = {
                    ...values,
                    celular: apenasDigitosCelular(values.celular),
                    // Campo validade_teste (payload): vem do estado selectedDate (sem input na tela).
                    validade_teste: selectedDate ? convertDataBrParaUS(selectedDate) : undefined,
                    // Campo data_proximo_contato (payload): vem do estado selectedDateProximoContato (sem input na tela).
                    data_proximo_contato:
                        selectedDateProximoContato && selectedDateProximoContato !== ''
                            ? convertDataEHoraBrParaUS(selectedDateProximoContato)
                            : undefined,
                    // Campo enviar_whatsapp: forçado true no envio (sem input na tela nesta página).
                    enviar_whatsapp: true,
                    // Campo enviar_contato_vendedor: forçado true no envio (sem input na tela nesta página).
                    enviar_contato_vendedor: true,
                };
                await CadastrePublicoFormService.postCadastroSemContrato(payload);
                onSuccess?.();
            } catch (error: any) {
                const msg = error?.response?.data?.message;
                notification.error({
                    message: 'Erro',
                    description: msg || 'Não foi possível salvar. Entre em contato com o suporte!',
                });
            } finally {
                setLoadingSubmit(false);
                refClickSubmitSalvar.current = false;
            }
        },
        [onSuccess, selectedDate, selectedDateProximoContato]
    );

    const submit = useCallback(() => {
        refClickSubmitSalvar.current = true;
        form.validateFields()
            .then((values: any) => {
                const nomePoder = encontrarPoderPorId(values.poder_id)?.nome;
                const nomeCargo = encontrarCargoPorId(values.cargo_id)?.nome;
                confirmarPoderCargo(nomePoder, nomeCargo, () => salvar(values));
            })
            .catch((error: any) => {
                refClickSubmitSalvar.current = false;
                notification.error({
                    message: 'Erro',
                    description: 'Preencha os campos obrigatórios!',
                });
            });
    }, [confirmarPoderCargo, encontrarCargoPorId, encontrarPoderPorId, form, salvar]);

    return {
        form,
        loadingInit,
        loadingSubmit,
        loadingPoder,
        loadingVendedores,
        loadingCidades,
        dadosPoderList,
        dadosCargoList,
        dadosVendedoresList,
        dataListCidades,
        msgUsuarioJaExiste,
        vendedorLocked: vendedorSelectBloqueado,
        onChangeInputTelefone,
        onDropdownVisibleChangeCidades,
        handleSearchCidades,
        handleScrollCidades,
        isSelectCidadeOpen,
        buscarCelularSeJaExiste,
        submit,
        removeAcento,
    };
}
